import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { faAnglesLeft } from '@fortawesome/free-solid-svg-icons';
import Tabs from '@frontend/Components/Tabs/Tabs';
import StatusBadge from '@frontend/Components/StatusBadge/StatusBadge';
import UnifiedTopBar from '@frontend/Components/TopBar/UnifiedTopBar';
import { createDetailViewTemplate } from '@frontend/Components/TopBar/templates';
import MilestoneService from '@frontend/AppSettings/services/MilestoneService';
import PipelineProjectService from '@frontend/AppSettings/services/PipelineProjectService';
import type { GetMatterByIdResponse } from '@frontend/Models/Matter/MatterRequestResponseModels';
// Kicks off the capability fetch that un-redacts money. NavBarAuth also does this, but relying on
// it couples this screen to the navbar being mounted — in the E2E harness it is NOT, and a deep
// link to /projectDetails/<id> stayed redacted forever for a financial user. Same doubled-up
// precedent as BidDetailPage, for the same reason.
import { useBidCapabilities } from '@frontend/BidSheets/useBidCapabilities';
import { fetchBid } from '@frontend/BidSheets/bidService';
import { moneyCompact, percent, useFinancialVisible } from '@frontend/BidSheets/financialDisplay';
import ProjectScheduleTab, { MILESTONE_LIMIT } from './Schedule/ProjectScheduleTab';
import FieldTimesheetTab from './Timesheet/FieldTimesheetTab';
import MyFieldTimesheetView from './Timesheet/MyFieldTimesheetView';
import CommercialTab from './Commercial/CommercialTab';
import ClassificationTab from './Classification/ClassificationTab';
import ProjectTeamTab from './Team/ProjectTeamTab';
import ProjectSitesTab from './Sites/ProjectSitesTab';
import '../MatterDetails.css';
import './PipelineProjectDetail.css';

/**
 * Detail screen for a project converted from a bid (Part II P3; tabs filled in P4).
 *
 * Three tabs, inherited from the demo's consolidation (be5508d2) as a product decision: crew
 * loading, change orders and force account are ONE commercial workspace rather than three peers,
 * and bid-vs-actual sits WITH the schedule because variance is read against progress.
 *
 * The header is the shared matters UnifiedTopBar card (MatterDetails.css +
 * createDetailViewTemplate + .matter-topbar): back control, "{id} {name}", ID / Created / Status pills. The A5 chip row sits INSIDE that
 * card and is built OMISSION-FIRST: the matter payload is the legacy legal shape where every
 * field is optional/nullable, so a chip whose source is absent renders nothing — never
 * "undefined", never a fabricated value. Money chips (Contract/Cost/Margin) ride the gated
 * BvA payload: on `permitted: false` they are ABSENT entirely, not three permanent dashes — the
 * schedule tab's explicit refusal already tells that story once.
 */
interface PipelineProjectDetailProps {
  matterid: string;
  /** The router's full `matter_by_id` payload — chips read it; no second fetch. */
  matter?: GetMatterByIdResponse;
}

// Team is a FOURTH tab, not a corner of one of the other three: a roster with add and remove is a
// workspace of its own, and a tab mounts lazily so its matter read costs nothing to the caller who
// never opens it. Deliberately NOT mirrored as a header chip — the chip row reads the router's
// `matter` prop, which the tab's own query does not refresh, so a count there would go stale the
// moment someone is added. A wrong number is worse than no number.
//
// Sites is a FIFTH tab for the same reason (P6): where work happens is reference data for the
// project, entered once and read by classification later, not a corner of the schedule. It mounts
// lazily like the others, which matters more here than elsewhere — nothing consumes a site until
// P7, so a caller who never opens the tab should pay nothing for it.
const TABS = [
  { id: 'schedule', label: 'Project Schedule' },
  { id: 'timesheet', label: 'Field Timesheet' },
  { id: 'commercial', label: 'Commercial' },
  { id: 'team', label: 'Team' },
  { id: 'sites', label: 'Sites' },
  // A SIXTH tab (P7b): what happens AFTER a day is judged — the off-baseline disposition queue,
  // the force-account release gate, and the reclassification report. It is not a corner of the
  // Commercial tab, because two of its three surfaces are money-free and open to the field
  // supervisor who recorded the work, while Commercial is the PM's pricing screen.
  { id: 'classification', label: 'Classification' },
];

/**
 * The "My Timesheet" sub-view (residual gap A7): the shared GeneralTimesheetView embedded
 * single-project, with the demo's Working Hours / Travel pair instead of the ENG 100–900 cost
 * codes. The rows come from the SAME server-scoped GET /get_all_billed_time the standalone
 * /financials/billing view uses — the A35 carve-out filters a permish ∉ {1,2} caller to their
 * OWN rows server-side, so an individual with no other permissions sees only their own time;
 * the props below merely narrow DISPLAY to this matter. Never swap this to the unscoped
 * GET /get_billed_time/<matter_id>, which has no row scoping. Writes stamp notes as
 * "working - Working Hours" / "travel - Travel"; the ENG cost-code view drops uncoded notes
 * into an internal "uncategorized" bucket it never renders, so these rows are ABSENT from that
 * grid's cells and totals (pre-existing behaviour for any uncoded note) — accepted, pipeline
 * matters were never described by the 100–900 codes and live in this tab instead. Conversely,
 * this view's Working Hours row is a catch-all: the caller's ENG-coded entries on this matter
 * surface under it, and editing that cell re-stamps the survivors with the custom label (the
 * shared view's multi-entry collapse). Own rates stay visible, identical to what the same
 * caller already sees at /financials/billing.
 * Module-scope constant: a fresh array literal per render would churn the child's memos.
 */

const num = (v: string | null | undefined): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * Calendar-only strings (YYYY-MM-DD, optionally with a time suffix) must be built as
 * local dates; otherwise `new Date('YYYY-MM-DD')` is UTC midnight and shows the prior
 * day in -UTC zones. Same parse as the matters top bar's Due helper — not its Created
 * helper, which uses `new Date(value)` and has that UTC-shift bug.
 */
const formatCreatedDate = (value?: string | null): string | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const date = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const PipelineProjectDetail = ({ matterid, matter }: PipelineProjectDetailProps): JSX.Element => {
  useBidCapabilities();   // the loader; the chips' moneyCompact() below and the tabs both subscribe
  useFinancialVisible();  // re-render the Contract/Cost/Margin chips when the capability resolves
  const navigate = useNavigate();
  const [tab, setTab] = useState('schedule');
  // Timesheet sub-view: crew day-tickets (the pre-A7 tab content) stays the default.
  const [timesheetView, setTimesheetView] = useState<'crew' | 'my'>('crew');

  const matterName = matter?.mattername;
  const bidId = matter?.bid_id ?? null;

  // SHARED KEYS, exactly the schedule tab's (the plan's query-key discipline): a near-miss key
  // would double the fetches and let two copies of one figure disagree mid-refetch.
  const milestones = useQuery({
    queryKey: ['ProjectMilestones', matterid],
    queryFn: () => MilestoneService.getProjectMilestones(matterid, { limit: MILESTONE_LIMIT }),
  });
  // Which frozen scope the figures are measured against (P4). `undefined` is the LATEST — "are we
  // on budget" — and 1 asks "how does this compare to what we originally bid". The version is part
  // of the query key: two versions are two different answers and must never share a cache entry.
  const [baselineVersion, setBaselineVersion] = useState<number | undefined>(undefined);
  const bva = useQuery({
    queryKey: ['BidVsActual', matterid, baselineVersion ?? 'latest'],
    queryFn: () => PipelineProjectService.getBidVsActual(matterid, baselineVersion),
  });
  // The source bid, for the Bid # chip — keyed on matters.bid_id (the same UUID CommercialTab's
  // MSA panel receives). Disabled with no bid_id: the chip is absent, not a failed fetch.
  const bid = useQuery({
    queryKey: ['BidHeader', bidId],
    queryFn: () => fetchBid(bidId as string),
    enabled: !!bidId,
  });

  // Narrowed ONCE (the pnlData pattern): `permitted` discriminates the union and the narrowing
  // does not survive repeated `bva.data` reads in the JSX.
  const bvaData = bva.data?.permitted ? bva.data : null;
  const milestoneRows = milestones.data?.data?.milestones ?? [];
  const completedCount = milestoneRows.filter((m) => m.status === 'completed').length;
  const progressPct = milestoneRows.length
    ? Math.round((completedCount / milestoneRows.length) * 100)
    : null;
  const bidNumber = bid.data?.bid.id;
  const created = formatCreatedDate(matter?.opendate);
  // Same title shape as MatterDetailsTopBar: "{id} {name}".
  const matterTitle = matterName ? `${matterid} ${matterName}` : matterid;

  const unifiedConfig = createDetailViewTemplate({
    leftExtras: [
      {
        type: 'button',
        icon: faAnglesLeft,
        onClick: () => navigate('/projects'),
        ariaLabel: 'Back to list',
      },
      <div className="matter-topbar__title" key="title-block">
        <div className="matter-topbar__name">{matterTitle}</div>
        <div className="matter-topbar__meta">
          <span className="matter-topbar__pill">ID: {matterid}</span>
          {created && (
            <span className="matter-topbar__pill">Created: {created}</span>
          )}
          {!!matter?.status && <StatusBadge status={matter.status} size="small" />}
        </div>
        <div className="pipeline-project-chips">
          {!!matter?.client_name && (
            <span className="pipeline-chip"><em>Client</em>{matter.client_name}</span>
          )}
          {/* country is nullable in the legal shape — omitted when unset, per the plan. */}
          {!!matter?.country && (
            <span className="pipeline-chip"><em>Location</em>{matter.country}</span>
          )}
          {/* The matter leader (matters.employeeid) — the closest real field, what the demo
              itself displayed. No per-project PM field exists and none is invented. */}
          {!!matter?.employee_name && (
            <span className="pipeline-chip"><em>PM</em>{matter.employee_name}</span>
          )}
          {/* Conversion often sets matterid = bid number; restating it as a Bid chip
              triples the same token (title, ID pill, chip). Show Bid only when it
              names a different id, as the demo's WVR-… / BID-… pair does. */}
          {!!bidNumber && bidNumber !== matterid && (
            <Link className="pipeline-chip pipeline-chip-link" to={`/bids/${bidNumber}`}>
              <em>Bid</em>{bidNumber}
            </Link>
          )}
          {bvaData && (
            <>
              {/* Contract/Margin read the BvA margin block, NOT matters.amount; both are null
                  on a project with no contract value and the chips omit rather than dash.
                  The figure is still projected (schedule tab 2.7); the label is "Margin"
                  to match the demo header. Compact dollars match that header too. */}
              {bvaData.margin?.contract != null && (
                <span className="pipeline-chip pipeline-chip-accent">
                  <em>Contract</em>{moneyCompact(num(bvaData.margin.contract))}
                </span>
              )}
              <span className="pipeline-chip">
                <em>Cost</em>{moneyCompact(num(bvaData.totals.actual))}
              </span>
              {bvaData.margin?.projected_margin_pct != null && (
                <span className="pipeline-chip pipeline-chip-good">
                  <em>Margin</em>{percent(num(bvaData.margin.projected_margin_pct))}
                </span>
              )}
              {/* Subcontracted scope, stated rather than folded in (P4). Silence here would be the
                  lie: planned money with no actuals tracked against it reads as under budget. */}
              {bvaData.subcontract && Number(bvaData.subcontract.planned) > 0 && (
                <span className="pipeline-chip" title="Bid value — actuals are not tracked for subcontracted scope">
                  <em>Sub</em>{moneyCompact(num(bvaData.subcontract.planned))}
                </span>
              )}
              {/* The version choice appears ONLY when there is one. A control that changes nothing
                  teaches the user it does nothing; on a project with one baseline the two answers
                  are identical by construction. */}
              {bvaData.baseline && bvaData.baseline.latest > 1 && (
                <label className="pipeline-chip">
                  <em>Measured vs</em>
                  <select
                    value={baselineVersion ?? ''}
                    onChange={(e) => setBaselineVersion(
                      e.target.value === '' ? undefined : Number(e.target.value))}
                  >
                    <option value="">Current scope (v{bvaData.baseline.latest})</option>
                    <option value="1">Original bid (v1)</option>
                  </select>
                </label>
              )}
            </>
          )}
          {/* Milestones completed/total — open to every member, unlike the BvA percent.
              Text only (demo header); the mini bar was ours and made the row taller. */}
          {milestoneRows.length > 0 && progressPct !== null && (
            <span className="pipeline-chip">
              <em>Progress</em>
              {completedCount}/{milestoneRows.length} ({progressPct}%)
            </span>
          )}
        </div>
      </div>,
    ],
    className: 'topBar matter-topbar',
    pageContext: {
      matterId: matterid,
      matterName,
    },
  });

  return (
    <div className="matter-details-page">
      <UnifiedTopBar {...unifiedConfig} />

      <div className="matter-tabs">
        <Tabs tabs={TABS} activeTabId={tab} onTabChange={setTab} variant="underline" />
      </div>

      {tab === 'schedule' && <ProjectScheduleTab matterid={matterid} matterName={matterName} />}
      {tab === 'timesheet' && (
        <div className="pipeline-timesheet-tab">
          <div className="pipeline-view-toggle pipeline-timesheet-switch" role="group" aria-label="Timesheet view">
            <button
              type="button"
              className={timesheetView === 'crew' ? 'pipeline-action-btn' : 'pipeline-secondary-btn'}
              aria-pressed={timesheetView === 'crew'}
              onClick={() => setTimesheetView('crew')}
            >
              Crew Field Tickets
            </button>
            <button
              type="button"
              className={timesheetView === 'my' ? 'pipeline-action-btn' : 'pipeline-secondary-btn'}
              aria-pressed={timesheetView === 'my'}
              onClick={() => setTimesheetView('my')}
            >
              My Timesheet
            </button>
          </div>
          {timesheetView === 'crew' ? (
            <FieldTimesheetTab matterid={matterid} />
          ) : (
            // P7a: re-pointed off the shared `GeneralTimesheetView`, which writes /log_time →
            // billed_time (the legal billing spine). A pipeline day recorded there could also
            // exist on the construction spine and bill twice; the server now refuses that door
            // for pipeline matters, and this is where the hours go instead.
            <MyFieldTimesheetView matterid={matterid} matterName={matterName} />
          )}
        </div>
      )}
      {tab === 'commercial' && <CommercialTab matterid={matterid} bidId={bidId} />}
      {tab === 'team' && <ProjectTeamTab matterid={matterid} />}
      {tab === 'sites' && <ProjectSitesTab matterid={matterid} />}
      {tab === 'classification' && <ClassificationTab matterid={matterid} />}
    </div>
  );
};

export default PipelineProjectDetail;
